/**
 * Auth endpoints (Laravel Fortify under /api/v1/auth) and GET /api/v1/me.
 * @typedef {import('./client.js').ApiClient} ApiClient
 *
 * @typedef {{
 *   id: number|string,
 *   name: string,
 *   email: string,
 *   roles: string[],
 *   permissions: string[],
 *   apps: { admin: boolean, portal: boolean },
 *   two_factor: { enabled: boolean, required: boolean },
 *   impersonating: boolean,
 * }} Me
 */

/** @param {ApiClient} client */
export function createAuthApi(client) {
  return {
    /** @returns {Promise<Me>} */
    async me() {
      const res = await client.get('/me');
      return res?.data ?? res;
    },

    /**
     * Fetches the CSRF cookie first, then logs in.
     * @param {{ email: string, password: string, remember?: boolean }} credentials
     * @returns {Promise<{ two_factor: boolean }>}
     */
    async login({ email, password, remember = false }) {
      await client.csrf();
      const res = await client.post('/auth/login', { email, password, remember });
      return { two_factor: Boolean(res?.two_factor) };
    },

    /** @param {{ code?: string, recovery_code?: string }} payload */
    twoFactorChallenge(payload) {
      const body = payload.recovery_code ? { recovery_code: payload.recovery_code } : { code: payload.code };
      return client.post('/auth/two-factor-challenge', body);
    },

    logout() {
      return client.post('/auth/logout');
    },

    /**
     * Beendet eine aktive Benutzeransicht (Impersonation) und kehrt zum Admin-Konto zurück.
     * @returns {Promise<Me>}
     */
    async stopImpersonation() {
      const res = await client.post('/impersonation/stop');
      return res?.data ?? res;
    },

    /** @param {{ email: string }} payload */
    async forgotPassword({ email }) {
      await client.csrf();
      return client.post('/auth/forgot-password', { email });
    },

    /** @param {{ token: string, email: string, password: string, password_confirmation: string }} payload */
    async resetPassword(payload) {
      await client.csrf();
      return client.post('/auth/reset-password', payload);
    },

    /** @param {{ current_password: string, password: string, password_confirmation: string }} payload */
    updatePassword(payload) {
      return client.put('/auth/user/password', payload);
    },

    /** @param {{ password: string }} payload */
    confirmPassword({ password }) {
      return client.post('/auth/user/confirm-password', { password });
    },

    enableTwoFactor() {
      return client.post('/auth/user/two-factor-authentication');
    },

    /** @param {{ code: string }} payload */
    confirmTwoFactor({ code }) {
      return client.post('/auth/user/confirmed-two-factor-authentication', { code });
    },

    /** @returns {Promise<{ svg: string, url: string }>} */
    twoFactorQrCode() {
      return client.get('/auth/user/two-factor-qr-code');
    },

    /** @returns {Promise<string[]>} */
    twoFactorRecoveryCodes() {
      return client.get('/auth/user/two-factor-recovery-codes');
    },

    regenerateRecoveryCodes() {
      return client.post('/auth/user/two-factor-recovery-codes');
    },

    disableTwoFactor() {
      return client.delete('/auth/user/two-factor-authentication');
    },
  };
}

/** @typedef {ReturnType<typeof createAuthApi>} AuthApi */
